/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { BaseService } from 'apps/console-core/src/Services/_base';
import { HttpResponse } from 'apps/console-core/src/Services/http';
import { Observable, of, switchMap } from 'rxjs';
import { EntityLogsResponse } from './Entities/LogsResponse/LogsResponse.entity';
import { LoggingServiceConfiguration } from './Logging.service.config';
import { LoggingServiceGetLogsDto, LoggingServiceSendLogsDto } from './Logging.service.dto';

/**
 * The logging service.
 * @export
 * @class LoggingService
 */
export class LoggingService extends BaseService {
  /**
   * Creates an instance of LoggingService.
   * @param {LoggingServiceConfiguration} configuration The configuration options to accept.
   * @memberof LoggingService
   */
  public constructor(configuration: LoggingServiceConfiguration) {
    super(configuration);
  }

  /**
   * Creates base api url for the Logging service.
   * @returns {string | null} The base api url for the Logging service.
   * @memberof LoggingService
   */
  getBaseServiceUrl(): string | null {
    // This service is deployed in multiple regions and
    // needs to use the tenant's region.
    return this.getServiceUrlByTenant(`logging`);
  }

  /**
   * Append query param to the given url.
   * @protected
   * @param {Array<string>} queryParams The list of query vars.
   * @param {string} url The url to append query params.
   * @returns {string} The url with query params appended.
   * @memberof LoggingService
   */
  protected appendQueryParamsToUrl(queryParams: Array<string>, url: string): string {
    if (queryParams && queryParams.length > 0) {
      return `${url}?${queryParams.join('&')}`;
    }

    return url;
  }

  /**
   * Creates api url for logs.
   * @returns {string | null} The api url for logs.
   * @memberof LoggingService
   */
  public createLoggingUrl(): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return baseUrl;
  }

  /**
   * Attempt to get a list of logs.
   * @param {LoggingServiceGetLogsDto} dto The DTO to get the logs.
   * @returns {Observable<EntityLogsResponse | null>} The observable of logs.
   * @memberof LoggingService
   */
  public getLogs(dto: LoggingServiceGetLogsDto): Observable<EntityLogsResponse | null> {
    const {
      filterFrom,
      filterHost,
      filterLevels,
      filterService,
      filterTag,
      filterTo,
      filterTraceId,
      pageCursor,
      pageLimit,
      searchValue,
      sort,
    } = dto;

    const queryParams: Array<string> = [];

    queryParams.push(`${encodeURIComponent('filter[from]')}=${filterFrom}`);
    queryParams.push(`${encodeURIComponent('filter[to]')}=${filterTo}`);

    if (filterHost) {
      queryParams.push(`${encodeURIComponent('filter[host]')}=${filterHost}`);
    }

    if (filterLevels) {
      filterLevels.forEach((level) => {
        queryParams.push(`${encodeURIComponent('filter[level]')}=${level}`);
      });
    }

    if (filterService) {
      queryParams.push(`${encodeURIComponent('filter[service]')}=${filterService}`);
    }

    if (filterTag) {
      filterTag.forEach((tag) => {
        queryParams.push(`${encodeURIComponent('filter[tag]')}=${tag}`);
      });
    }

    if (filterTraceId) {
      queryParams.push(`${encodeURIComponent('filter[traceid]')}=${filterTraceId}`);
    }

    if (pageCursor) {
      queryParams.push(`${encodeURIComponent('page[cursor]')}=${pageCursor}`);
    }

    if (pageLimit) {
      queryParams.push(`${encodeURIComponent('page[limit]')}=${pageLimit}`);
    }

    if (searchValue) {
      // Currently only text is supported.
      // In the future, regex may be added.
      queryParams.push(`${encodeURIComponent(`search[type]`)}=text`);
      queryParams.push(`${encodeURIComponent(`search[value]`)}=${searchValue}`);
    }

    if (sort) {
      const { direction, property } = sort;
      queryParams.push(`sort=${encodeURIComponent(direction)}${property}`);
    }

    const baseLogsUrl = this.createLoggingUrl();
    if (!baseLogsUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, baseLogsUrl);
    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityLogsResponse> => {
          return this._handleResponseError<EntityLogsResponse>(value);
        }),
      );
  }

  /**
   * Attempt to send a list of logs.
   * @param {LoggingServiceSendLogsDto} dto The DTO to send the logs.
   * @returns {Observable<{} | null>} The observable response.
   * @memberof LoggingService
   */
  public sendLogs(dto: LoggingServiceSendLogsDto): Observable<{} | null> {
    const { logs } = dto;

    const logBody = logs.map((log) => {
      const { host, level, message, service, tags, timestamp, traceId, userId } = log;

      const logObject = {
        host,
        level,
        service,
        timestamp,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as Record<string, any>;

      // Optional properties
      if (message) {
        logObject.message = message;
      }

      if (tags) {
        logObject.tags = tags.join(` `);
      }

      if (traceId) {
        logObject.traceid = traceId;
      }

      if (userId) {
        logObject.userid = userId;
      }

      return logObject;
    });

    const requestUrl = this.createLoggingUrl();
    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .post<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
        body: logBody,
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<{}> => {
          return this._handleResponseError<Array<{}>>(value);
        }),
      );
  }
}
